<style lang="scss" src='./SensorData.scss' scoped></style>
<template src="./SensorData.html"></template>

<script>
import ApexChart from 'vue-apexcharts';
import {
  getSensorData,
  getSensorGraph,
} from '@/api/index';

import moment from 'moment';
import i18n from './../../../../i18n';
import messages from './SensorData.i18n';

// Parameters shown in the tiles and the chart switcher.
const PARAMS = [
  { key: 'vwc', label: 'alerts.Moisture', uom: 'MOISTURE', color: '#1E86C4', decimals: 1 },
  { key: 'temperature', label: 'alerts.Temperature', uom: 'TEMP', color: '#F5842A', decimals: 1 },
  { key: 'bulk_ec', label: 'alerts.Salinity', uom: 'SALINITY', color: '#0FA79B', decimals: 2 },
  { key: 'batt_volt', label: 'sd.battery', uom: null, color: '#1F7A44', decimals: 2 },
  { key: 'rssi', label: 'general.signalStrength', uom: 'RSSI', color: '#5C6B70', decimals: 0 },
];

// History window -> resample bin (keeps 90 days to a few hundred points).
const RANGES = [
  { days: 7, resample: '1h' },
  { days: 30, resample: '3h' },
  { days: 90, resample: '12h' },
];

export default {
  name: 'SensorData',
  i18n: { messages },
  props: {
    sensor: {
      required: false,
    },
  },
  components: {
    ApexChart,
  },
  data() {
    return ({
      params: PARAMS,
      ranges: RANGES,
      selectedParam: 'vwc',
      selectedRange: 7,
      showRaw: false,
      showDaily: false,
      openDay: null,
      dailyHistory: [],
      visible: false,
      history: [],
      sensorData: [],
      pdfName: null,
      pagination: {
        sensorData: {
          page: 0,
          limit: 100,
          next: null
        }
      },
      loading: {
          sensorData: false,
          history: false,
          daily: false,
        },
    });
  },
  computed: {
    param() {
      return PARAMS.find((p) => p.key === this.selectedParam);
    },

    tiles() {
      return PARAMS.map((p) => {
        const points = this.historyPoints(p);
        const last = points.length ? points[points.length - 1] : null;
        let change = null;
        if (last) {
          const dayAgo = last.x - 24 * 3600 * 1000;
          const before = points.filter((pt) => pt.x <= dayAgo).pop();
          if (before) {
            change = last.y - before.y;
          }
        }
        return {
          ...p,
          value: last ? this.formatNumber(p, last.y, true) : '–',
          change: change === null ? null : `${change > 0 ? '+' : ''}${change.toFixed(p.decimals)}`,
        };
      });
    },

    // Last 30 days, one row per day; each row opens into its hourly averages.
    dailyRows() {
      const keys = ['vwc', 'temperature', 'bulk_ec'];
      const ps = keys.map((k) => PARAMS.find((p) => p.key === k));
      const byDay = {};
      this.dailyHistory.forEach((r) => {
        const t = moment(r.reading_time);
        (byDay[t.format('YYYY-MM-DD')] = byDay[t.format('YYYY-MM-DD')] || []).push(r);
      });
      const cell = (p, vals) => {
        const nums = vals.map((v) => this.toNumber(p, v)).filter((v) => v !== null && Number.isFinite(v));
        return nums.length ? this.formatNumber(p, nums.reduce((a, b) => a + b, 0) / nums.length, true) : '–';
      };
      return Object.keys(byDay).sort().reverse().map((day) => {
        const rows = byDay[day].slice().sort((a, b) => new Date(a.reading_time) - new Date(b.reading_time));
        return {
          day,
          label: moment(day).format('ddd, MMM D'),
          values: ps.map((p) => cell(p, rows.map((r) => r[p.key]))),
          hours: rows.map((r) => ({
            hour: moment(r.reading_time).format('HH:mm'),
            values: ps.map((p) => cell(p, [r[p.key]])),
          })),
        };
      });
    },

    chartSeries() {
      const p = this.param;
      return [{ name: this.$t('sd.measured'), type: 'line', color: p.color, width: 2, data: this.historyPoints(p) }];
    },

    chartOptions() {
      const p = this.param;
      const series = this.chartSeries;
      const unit = p.uom ? this.G_getUOM({ uom: this.consts.UOM[p.uom] }) : 'V';
      const yAnnotations = [];
      const threshold = this.sensor && this.sensor.threshold;
      if (threshold && (p.key === 'vwc' || p.key === 'temperature')) {
        const field = p.key === 'vwc' ? 'moisture' : 'temperature';
        ['min', 'max'].forEach((side) => {
          const val = threshold[`${side}_${field}`];
          if (val !== null && val !== undefined && val !== '') {
            const y = this.toNumber(p, val);
            yAnnotations.push({
              y,
              borderColor: '#9AA7AC',
              strokeDashArray: 4,
              label: { text: `${side === 'min' ? 'Min' : 'Max'} ${y}${unit}`, style: { color: '#5C6B70', background: 'transparent' }, borderWidth: 0 },
            });
          }
        });
      }
      return {
        chart: {
          type: 'line',
          height: 320,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: 'inherit',
        },
        colors: series.map((s) => s.color),
        stroke: { curve: 'straight', width: series.map((s) => s.width) },
        markers: { size: 0, hover: { size: 4 } },
        dataLabels: { enabled: false },
        legend: { show: series.length > 1, position: 'top', horizontalAlign: 'left' },
        grid: { borderColor: '#E4EAEA', strokeDashArray: 0 },
        xaxis: {
          type: 'datetime',
          labels: { datetimeUTC: false, style: { colors: '#5C6B70' } },
          axisBorder: { color: '#E4EAEA' },
          tooltip: { enabled: false },
        },
        yaxis: {
          decimalsInFloat: p.decimals,
          forceNiceScale: true,
          title: { text: unit, style: { color: '#5C6B70', fontWeight: 400 } },
          labels: { style: { colors: '#5C6B70' } },
        },
        annotations: { yaxis: yAnnotations },
        tooltip: {
          shared: true,
          intersect: false,
          x: { format: 'MMM dd, HH:mm' },
          y: { formatter: (v) => (v === null || v === undefined ? '' : `${Number(v).toFixed(p.decimals)} ${unit}`) },
        },
        noData: { text: this.loading.history ? this.$t('sd.loading') : this.$t('sd.noHistory') },
      };
    },

    sensorDataFiltered: {
      get() {
          let sensorDataAux = this.sensorData;
          if (this.sensorData) {

            const rowsPerPage = this.pagination.sensorData.limit;
            if (this.sensorData.length > rowsPerPage) {
              const currentPage = this.pagination.sensorData.page;
              const start = (currentPage * rowsPerPage) - rowsPerPage;
              const end = start + rowsPerPage;
              sensorDataAux = this.sensorData.slice(start, end)
            }
          }

          // Set Flag newDay to items in order to know if the next element belongs to same date that the back element
          if (sensorDataAux && sensorDataAux.length > 0) {
            let lastReadingTime = null;
            sensorDataAux = sensorDataAux.map((sensorData) => {
              const readingTimeObj = new Date(sensorData.reading_time);
              let newDay = true;
              if (lastReadingTime) {
                newDay = !(readingTimeObj.getFullYear() === lastReadingTime.getFullYear() &&
                        readingTimeObj.getMonth() === lastReadingTime.getMonth() &&
                        readingTimeObj.getDate() === lastReadingTime.getDate());
                lastReadingTime = newDay ? readingTimeObj : lastReadingTime;
              } else {
                lastReadingTime = readingTimeObj
              }
              return {...sensorData, newDay: newDay};
            });
          }
          return sensorDataAux;
      },
    },

    startRecords: function(){
      const paginationSD = this.pagination.sensorData;
      return (paginationSD.page * paginationSD.limit) - paginationSD.limit + 1;
    },

    endRecords: function(){
      const paginationSD = this.pagination.sensorData;
      const end = paginationSD.page * paginationSD.limit;
      return end > this.sensorData.length ? this.sensorData.length : end;
    },

    canGetSensorDataFromStore: function(){
      const paginationSD = this.pagination.sensorData;
      const start = (((paginationSD.page + 1) * paginationSD.limit) - paginationSD.limit) + 1;
      return start <= this.sensorData.length;
    },

  },
  watch: {
    sensor: function (newSensor, oldSensor) {
      if (newSensor && oldSensor && newSensor.sensor_id !== oldSensor.sensor_id) {
        this.dailyHistory = [];
        this.openDay = null;
        if (this.showDaily) {
          this.loadDaily();
        }
        this.loadData();
      }
    },
    selectedRange: function () {
      this.loadHistory();
    },
    showDaily: function (open) {
      if (open && !this.dailyHistory.length) {
        this.loadDaily();
      }
    },
    showRaw: function (open) {
      if (open && !this.sensorData.length) {
        this.loadSensorData();
      }
    },
  },

  methods: {
    // Value in the course's units, as a number (battery is volts, no conversion).
    toNumber(p, val) {
      if (val === null || val === undefined || val === '') {
        return null;
      }
      if (!p.uom) {
        return Number(Number(val).toFixed(p.decimals));
      }
      if (p.uom === 'TEMP') {   // the shared converter rounds to whole degrees; keep a decimal for charts
        const course = this.G_getFromStore('course');
        const f = Number(val);
        const t = course && course.measurement_system === 'METRIC' ? ((f - 32) * 5) / 9 : f;
        return Number(t.toFixed(p.decimals));
      }
      const n = this.G_getMeasurement({ val, uom: this.consts.UOM[p.uom], addUnit: false, invertUOM: false });
      return n === '' ? null : Number(Number(n).toFixed(p.decimals));
    },

    formatNumber(p, n, addUnit) {
      const unit = p.uom ? this.G_getUOM({ uom: this.consts.UOM[p.uom] }) : 'V';
      return `${Number(n).toFixed(p.decimals)}${addUnit ? ` ${unit}` : ''}`;
    },

    historyPoints(p) {
      return this.history
        .filter((r) => r[p.key] !== null && r[p.key] !== undefined && r[p.key] !== ''
          && !(p.key === 'rssi' && Number(r[p.key]) >= 0))   // rssi >= 0 is an invalid reading
        .map((r) => ({ x: new Date(r.reading_time).getTime(), y: this.toNumber(p, r[p.key]) }));
    },

    async loadDaily() {
      if (!this.sensor) {
        return;
      }
      const sensorId = this.sensor.sensor_id;
      this.loading.daily = true;
      try {
        const rows = await getSensorGraph({ sensorId, alias: 'all', until: 30, resampleFreq: '1h' });
        if (this.sensor && this.sensor.sensor_id === sensorId) {
          this.dailyHistory = rows || [];
        }
      } catch (e) {
        this.dailyHistory = [];
      }
      this.loading.daily = false;
    },

    async loadHistory() {
      if (!this.sensor) {
        return;
      }
      const range = RANGES.find((r) => r.days === this.selectedRange);
      const sensorId = this.sensor.sensor_id;
      this.loading.history = true;
      try {
        const rows = await getSensorGraph({ sensorId, alias: 'all', until: range.days, resampleFreq: range.resample });
        if (this.sensor && this.sensor.sensor_id === sensorId) {
          this.history = rows || [];
        }
      } catch (e) {
        this.history = [];
      }
      this.loading.history = false;
    },

    downloadCSV() {
      let csvContent = "data:text/csv;charset=utf-8,";
      let arrData = this.getCsvAllData();
      if(arrData.length > 0){
          csvContent += [
              Object.keys(arrData[0]).join(","),
              ...arrData.map(item => Object.values(item).join(","))
          ]
              .join("\n")
              .replace(/(^\[)|(\]$)/gm, "");

          const data = encodeURI(csvContent);
          const link = document.createElement("a");
          link.setAttribute("href", data);
          link.setAttribute("download", this.pdfName + ".csv");
          link.click();
      }
    },
    getCsvAllData(){
      let readings = this.sensorDataFiltered;
      if(readings.length>0){
        let data = [];
        readings.forEach((sensorData)=>{
          let obj = {}
          let last_reading = this.G_formatDate({date: sensorData.reading_time, pretty: false});
          obj[i18n.t('general.date')] = last_reading ? last_reading.replace(/,/g, "") : last_reading;
          let temperature = this.G_getMeasurement({ val: sensorData.temperature,
                      uom: this.consts.UOM.TEMP,
                      addUnit: true, invertUOM: false });
          obj[i18n.t('alerts.Temperature')] = temperature.replace('°', '');
          obj[i18n.t('alerts.Salinity')] = this.G_getMeasurement({ val: sensorData.bulk_ec,
                      uom: this.consts.UOM.SALINITY,
                      addUnit: true, invertUOM: false });
          obj[i18n.t('alerts.Dielectric')] = this.G_getMeasurement({ val: sensorData.dielectric,
                      uom: this.consts.UOM.DIELECTRIC,
                      addUnit: true, invertUOM: false });
          obj[i18n.t('alerts.Moisture')] = this.G_getMeasurement({ val: sensorData.vwc,
                      uom: this.consts.UOM.MOISTURE,
                      addUnit: true, invertUOM: false });

          data.push(obj);
        })
        let headers = [];
        //let filters = this.getHeadersObj();
        //headers.push(filters);
        return [...headers, ...data];
      }
      return [];
    },

    scrollToTopSensorDataTbl() {
      const dateHeaderElement = document.querySelector('.date-header:first-child');
      if (dateHeaderElement) {
        dateHeaderElement.scrollIntoView();
      }
    },

    changeSensorDataPage(newPage) {
      this.pagination = {...this.pagination, sensorData: {
        ...this.pagination.sensorData,
        page: newPage
      }};
      this.scrollToTopSensorDataTbl();
    },

    backSensorDataPage() {
      this.changeSensorDataPage(this.pagination.sensorData.page - 1);
    },

    nextSensorDataPage() {
      if (this.canGetSensorDataFromStore) {
        this.changeSensorDataPage(this.pagination.sensorData.page + 1);
      } else {
        this.loadSensorData();
      }
    },

    resetSensorDataPagination() {
      this.pagination = {...this.pagination, sensorData: {
        ...this.pagination.sensorData,
        next: null,
        page: 0
      }};
    },

    async loadSensorData() {
      this.loading.sensorData = true;

      let pagination = this.pagination.sensorData;
      await getSensorData(this.sensor.sensor_id, {
        limit: pagination.limit,
        next: pagination.next
      }).then((response) => {
        this.pagination.sensorData = {
          ... this.pagination.sensorData,
          next: response.next_page,
          page: ((response.rows && response.rows.length) ?
            this.pagination.sensorData.page + 1 : this.pagination.sensorData.page)
        };

        if (this.pagination.sensorData.page > 1) {
          this.sensorData = [...this.sensorData, ...response.rows];
        } else {
          this.sensorData = response.rows;
        }

        this.loading.sensorData = false;
      });
    },

    reloadRawData() {
      this.sensorData = [];
      this.resetSensorDataPagination();
      this.loadSensorData();
    },

    loadData() {
      if (process.env.VUE_APP_SENTRY_ENV == "production") {
        this.$analytics.setCurrentScreen("Web Sensors Data");
        this.$analytics.logEvent("web_sensors_data");
      }

      this.history = [];
      this.sensorData = [];
      this.resetSensorDataPagination();
      if (this.sensor){
        this.loadHistory();
        if (this.showRaw) {
          this.loadSensorData();
        }
      }
    },
  },

  beforeCreate() {},

  created() {
    let date = new Date();
    date = moment(date).format("MM-DD-YYYY");
    this.pdfName = this.sensor.serial_number + '_' + i18n.t('sensors.data') + '_' + date;
  },

  mounted() {
    this.loadData();
    // The tab is mounted while hidden; draw the chart only once it is on screen so ApexCharts
    // measures a real width.
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.visible = true;
        }
      });
      this.observer.observe(this.$el);
    } else {
      this.visible = true;
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>
